/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import { Stack } from "@mui/material";
import SoftButton from "components/SoftButton";
import TableCommon from "components/TableCommon";

const years = Array.from({ length: 10 }, (_, i) => ({
  value: String(new Date().getFullYear() - i),
  label: String(new Date().getFullYear() - i),
}));

const months = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: `Tháng ${i + 1}`,
}));

function DataTable({
  table,
  pagination,
  isSorted,
  noEndBorder,
  query,
  setQuery,
  paginationData,
  setPaginationData,
  loading,
  filters,
  setFilters,
  onSync,
  syncing,
  onFetchData,
  accounts = [],
  accountsLoading = false,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    setPageSize,
  } = tableInstance;

  useEffect(() => setPageSize(paginationData.size), [paginationData.size, setPageSize]);

  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onFetchData(localFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      year: String(new Date().getFullYear()),
      month: String(new Date().getMonth() + 1),
      accountCode: "",
      searchBy: "",
    };
    setLocalFilters(resetFilters);
    setFilters(resetFilters);
    onFetchData(resetFilters);
  };

  const btnStyle = {
    height: "39px",
    borderRadius: 0,
    textTransform: "none",
    fontWeight: "bold",
  };

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <SoftBox lineHeight={1} width="100%">
          <Stack spacing={1} direction="row" alignItems="flex-end" flexWrap="wrap">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Năm
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftSelect
                  value={years.find((y) => y.value === localFilters.year)}
                  options={years}
                  onChange={(opt) => setLocalFilters((prev) => ({ ...prev, year: opt.value }))}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tháng
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftSelect
                  value={months.find((m) => m.value === localFilters.month)}
                  options={months}
                  onChange={(opt) => setLocalFilters((prev) => ({ ...prev, month: opt.value }))}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tài khoản phải trả
              </SoftTypography>
              <select
                value={localFilters.accountCode}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, accountCode: e.target.value }))}
                style={{ width: 250, height: 40, borderRadius: 8, border: "1px solid #d2d6da", padding: "0 8px", fontSize: "0.875rem" }}
              >
                <option value="">
                  {accountsLoading ? "Đang tải tài khoản..." : "Chọn tài khoản 331"}
                </option>
                {accounts.map((item) => (
                  <option key={item.accountCode} value={item.accountCode}>
                    {item.accountCode} - {item.accountName}
                  </option>
                ))}
              </select>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tìm kiếm
              </SoftTypography>
              <SoftBox width="12rem">
                <SoftInput
                  placeholder="Số CT, diễn giải, TK đối ứng"
                  value={localFilters.searchBy}
                  onChange={({ currentTarget }) => {
                    setLocalFilters((prev) => ({ ...prev, searchBy: currentTarget.value }));
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftButton
              variant="contained"
              style={{ ...btnStyle, backgroundColor: "#1a73e8", color: "white" }}
              onClick={handleApply}
              disabled={loading || accountsLoading}
            >
              Tải dữ liệu
            </SoftButton>

            <SoftButton
              variant="outlined"
              style={{ ...btnStyle, backgroundColor: "white", color: "#344767", border: "1px solid #d2d6da" }}
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </SoftButton>

            {onSync && (
              <SoftButton
                variant="contained"
                style={{ ...btnStyle, backgroundColor: "#4CAF50", color: "white" }}
                onClick={onSync}
                disabled={loading || syncing}
              >
                {syncing ? "Đang đồng bộ..." : "Đồng bộ dữ liệu"}
              </SoftButton>
            )}
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={setQuery}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        pagination={pagination}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

DataTable.propTypes = {
  query: PropTypes.any,
  setQuery: PropTypes.any,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
      "white",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
